import { Location } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

// RxJS
import { BehaviorSubject, catchError, combineLatest, defer, distinctUntilChanged, filter, forkJoin, map, Observable, of, repeat, Subject, switchMap, take, takeUntil, tap, timer } from 'rxjs';

// Lodash
import { cloneDeep, isEqual } from 'lodash-es';

// SocketIO
import { io, Socket } from 'socket.io-client';

// Services
import { ProjectService } from '@modules/project/services/project.service';
import { RecordsService } from '@modules/records/services/records.service';
import { StateService } from '@modules/settings/services/state.service';
import { UsersService } from '@modules/users/services/users.service';
import { PreviewService } from '@modules/preview/services/preview.service';
import { AlertService } from '@modules/elements/services/alert.service';
import { NotificationService } from '@modules/elements/services/notification.service';
import { Clipboard } from '@angular/cdk/clipboard';

// Components
import { PlaybackProgressBarComponent } from '@modules/preview/components/playback-progress-bar/playback-progress-bar.component';
import { PlaybackInOutPointsComponent } from '@modules/preview/components/playback-in-out-points/playback-in-out-points.component';
import { AudioLevelsComponent } from '@modules/preview/components/audio-levels/audio-levels.component';
import { RecordTestFilenameComponent } from './record-test-filename/record-test-filename.component';
import { CreateRecordComponent } from '../create-record/create-record.component';
import { PreviewComponent } from '@modules/preview/components/preview/preview.component';
import { PreviewCcComponent } from '@modules/preview/components/preview-cc/preview-cc.component';
import { PlaybackActionsComponent } from '@modules/preview/components/playback-actions/playback-actions.component';
import { TranscodeInOutPointsComponent } from '@modules/preview/components/transcode-in-out-points/transcode-in-out-points.component';

// Types
import { Project } from '@modules/project/types/project';
import { Record, RecordServer, RecordStatus, RecordTask, RecordTaskType } from '@modules/records/types/record';
import { RecordEvent } from '@modules/records/types/record-event';
import { UserPermissions } from '@modules/users/types/user';
import { StreamStatus } from '@modules/preview/types/stream-status';
import { DeckStatus } from '@modules/preview/types/deck-status';
import { Clip } from '@modules/preview/types/clip';
import { StreamSettings } from '@modules/preview/types/stream-settings';
import { Option } from '@modules/elements/types/option';
import { CcType } from '@modules/preview/types/captions';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';

// Env
import { environment } from '@environment';


type PreviewType = 'live' | 'playback';

interface PreviewModel {
  type: PreviewType,
  serverId?: string,
  channel: number,
  status?: StreamStatus,
  settings?: StreamSettings,
  clip?: Clip,
  isTrim: boolean,
  isOvertimeTrim: boolean,
  isManualUpload: boolean,
  isS3Download: boolean,
  isS3DownloadInProgress: boolean,
  isICap: boolean
  
}
@Component({
    selector: 'app-record-logs',
    templateUrl: './record-logs.component.html',
    styleUrl: './record-logs.component.less',
    standalone: false
})
export class RecordLogsComponent implements OnInit, OnDestroy {

  // ViewChild
  @ViewChild('timecode', { static: false }) timecodeRef?: ElementRef<HTMLElement>;
  @ViewChild(PlaybackActionsComponent, { static: false }) playbackActionsRef?: PlaybackActionsComponent;
  @ViewChild(PlaybackProgressBarComponent, { static: false }) progressBarRef?: PlaybackProgressBarComponent;
  @ViewChildren(PlaybackInOutPointsComponent) inOutPointsRefs?: QueryList<PlaybackInOutPointsComponent>;
  @ViewChildren(TranscodeInOutPointsComponent) inOutTranscodePointsRefs?: QueryList<TranscodeInOutPointsComponent>;
  @ViewChild(AudioLevelsComponent, { static: false }) audioLevels?: AudioLevelsComponent;
  @ViewChild(PreviewComponent, { static: false }) previewRef?: PreviewComponent;
  @ViewChildren(PreviewCcComponent) ccRef?: QueryList<PreviewCcComponent>;
  @ViewChild('ccTypeMenuButton', { read: MatMenuTrigger, static: false}) ccTypeMenuButton?: MatMenuTrigger;

  // Public
  public record?: Record;
  public recordEvents: RecordEvent[] = [];
  public displayRecordEvents: RecordEvent[] = [];
  public project?: Project;
  public permissions?: UserPermissions[];
  public preview: PreviewModel = {
    type: 'live',
    serverId: undefined,
    channel: 0,
    status: undefined,
    settings: undefined,
    clip: undefined,
    isTrim: false,
    isOvertimeTrim: false,
    isManualUpload: false,
    isS3Download: false,
    isS3DownloadInProgress: false,
    isICap: false,
  };
  public audio = {
    enable: false,
    pair: 0,
    volume: 1
  };
  public servers: {[id: string]: {
    playbackStatus?: StreamStatus;
    recordStatus?: StreamStatus;
    clip?: Clip
  }} = {};
  public search = {
    text: '',
    serverId: ''
  };
  public showCC = false;
  public showInfo = false;
  public s3DownloadUrl = '';
  public ccType: CcType = 'cc1';
  public ccTypes: CcType[] = ['cc1'];
  public actions = {
    irisUpload: {
      show: false,
      enabled: false,
      done: false
    },
    irisOvertimeUpload: {
      show: false,
      enabled: false,
      done: false
    },
    resetTasks: {
      show: false,
      enabled: false,
      done: false
    },
    resetOvertimeTasks: {
      show: false,
      enabled: false,
      done: false
    }
  }
  public display = {
    preview: true,
    s3DownloadInput: false
  };
  public canRestoreRecord: boolean = false;

  // Private
  private alive = new Subject<void>();
  private sockets = new Map<string, Socket>();
  private accessToken?: string;
  private projectId = new BehaviorSubject<string | undefined>(undefined);
  private serverIds = new BehaviorSubject<string[]>([]);
  private decksStatus = new Map<string, BehaviorSubject<DeckStatus | undefined>>();
  private updateSelectPreview = new Subject<void>();
  private s3DownloadRequested = false;

  /**
   * Constructor
   */

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private recordsService: RecordsService,
    private projectService: ProjectService,
    private stateService: StateService,
    private usersService: UsersService,
    private previewService: PreviewService,
    private alertService: AlertService,
    private notificationService: NotificationService,
    private location: Location,
    private dialog: MatDialog,
    private clipboard: Clipboard,
  ) { }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        takeUntil(this.alive),
        switchMap((params: ParamMap) => {
          const id = params.get('id');
          return this.recordsService.getRecord(id)
        }),
        tap(record => {
          this.record = record;
          if (this.record?.servers) {
            this.record.servers = Record.sortServers(this.record?.servers);
          }
          this.record?.servers?.forEach(server => server.tasks = Record.sortTasks(server.tasks));
          const serverIds = this.record?.servers?.map(server => server.id) || [];
          if (serverIds.length && (!this.preview.serverId || !isEqual(serverIds, this.serverIds.value))) {
            setTimeout(() => this.getStream(), 1000);
          }
          this.canRestoreRecord = new Date(this.record?.startTime || 0).getTime() > Date.now();
          this.projectId.next(this.record?.projectId);
          this.updateActions();
          this.updatePreviewFlags();
        }),
        switchMap(record => record?.id ? this.recordsService.getRecordEvents(record?.id) : of()),
        takeUntil(this.alive)
      )
      .subscribe(recordEvent => {
        this.recordEvents = recordEvent;
        this.filterRecordEvents();
      });
    // Get Token
    this.stateService.getState()
      .pipe(take(1))
      .subscribe(state => {
        this.accessToken = state.accessToken;
        setTimeout(() => this.getStream(), 1000);
      });
    // Get Current Project
    this.projectId.asObservable()
      .pipe(
        filter(projectId => !!projectId),
        switchMap(projectId => this.projectService.getProject(projectId!)),
        takeUntil(this.alive)
      )
      .subscribe(project => {
        this.project = project;
      });
    // Get Permissions
    this.usersService.getPermissions()
      .pipe(takeUntil(this.alive))
      .subscribe(permissions => this.permissions = permissions);
    // Get Status
    this.serverIds.asObservable()
      .pipe(
        switchMap(serverIds => {
          let observables: {[id: string]: Observable<any>} = {};
          this.decksStatus.forEach((deckStatus, serverId) => {
            observables[serverId] = deckStatus.asObservable()
              .pipe(
                map(status => {
                  // console.log('[1][STATUS] ', status);
                  const server = this.record?.servers?.find(item => item.id === serverId);
                  if (!this.servers[serverId]) {
                    this.servers[serverId] = {};
                  }
                  if (status?.inputs && server) {
                    this.servers[serverId].recordStatus = status?.inputs[server.recordChannel];
                    this.servers[serverId].playbackStatus = status?.inputs[server.playbackChannel];
                  }
                  if (this.preview.serverId === serverId) {
                    this.preview.status = this.preview.type === 'live' ? this.servers[serverId].recordStatus : this.servers[serverId].playbackStatus;
                  }
                  return this.servers[serverId].playbackStatus?.playback?.filename;
                }),
                distinctUntilChanged(isEqual),
                switchMap(filename => {
                  console.log('[2][FILENAME] ', filename);
                  if (this.record?.id && serverId) {
                    return defer(() => this.previewService.getClip(this.record!.id!, [serverId], filename).pipe(catchError(() => of(undefined))))
                      .pipe(repeat({ delay: () => timer(this.servers[serverId]?.clip ? 30000 : 5000) }));
                  }
                  return of(undefined);
                }),
                tap(clip => {
                  this.servers[serverId].clip = clip;
                  if (this.preview.serverId === serverId) {
                    this.preview.clip = clip;
                  }
                })
              );
          });
          console.log('[observables] ', observables);
          return combineLatest(observables);
        }),
        takeUntil(this.alive),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
    this.sockets.forEach(socket => socket.disconnect());
    this.sockets.clear();
    this.decksStatus.forEach(subject => subject.complete());
    this.decksStatus.clear();
  }

  /**
   * Methods
   */

  getDurationLabel(): string {
    const value = this.record?.durationValue;
    if (!value) {
      return '—';
    }
    if (this.record?.durationType === 'seconds') {
      const totalSeconds = +value;
      if (!isFinite(totalSeconds) || totalSeconds <= 0) return '—';
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.round((totalSeconds % 3600) / 60);
      if (hours && minutes) return `${hours}h ${minutes}m`;
      if (hours) return `${hours}h`;
      return `${minutes}m`;
    }
    return `${value} ${this.record?.durationType}`;
  }

  getMetadataCount(): number {
    return Object.keys(this.record?.additionalMetadata || {}).filter(k => !!this.record?.additionalMetadata?.[k]).length;
  }

  getStream(): void {
    if (!this.preview.serverId && this.record && this.record.servers && this.record.servers[0]) {
      this.preview.serverId = this.record.servers[0].id;
      this.selectPreview(this.preview.type, this.preview.serverId);
    }

    if (!this.record?.id || !this.accessToken || !this.preview.serverId) {
      return;
    }

    this.record.servers?.forEach(server => {
      if (!this.decksStatus.has(server.id)) {
        this.decksStatus.set(server.id,  new BehaviorSubject<DeckStatus | undefined>(undefined));
      }

      const address = (environment.ssl ? 'wss' : 'ws') + '://' + environment.api;
      const url = `${address}/records/cinedeck/status?id=${this.record!.id}&token=${this.accessToken}&serverId=${server.id}`;
      
      console.log(`[URL][STATUS][${server.id}] `, url);
      if (this.sockets.has(server.id)) {
        const socket = this.sockets.get(server.id);
        if ((socket?.io as any).uri !== url) {
          (socket?.io as any).uri = url;
          socket?.disconnect().connect();
        }
      } else {
        const socket = io(url, {transports: ['websocket'], forceNew: true});
        socket.on("status", (data) => {
          const parsedJson = JSON.parse(new TextDecoder().decode(data as ArrayBuffer));
          if (parsedJson.event === 'status') {
            this.decksStatus.get(server.id)?.next(parsedJson.data);
            // console.log(`[Status][${server.id}]: `, this.decksStatus.get(server.id)?.value);
          }
        });
        this.sockets.set(server.id, socket);
      }
    });
    console.log('[SERVERS] ', this.sockets.entries());
    const serverIds = this.record?.servers?.map(server => server.id) || [];
    if (!isEqual(serverIds, this.serverIds.value)) {
      this.serverIds.next(serverIds);
    }
  }

  getTaskTitle(type?: RecordTaskType): string | undefined {
    switch (type) {
      case 'updateCinedeckSettings': return 'Cinedeck update settings';
      case 'recording': return 'Recording';
      case 'vodTrim': return 'Trim VOD J2K';
      case 'vodMetadata': return 'Metadata VOD J2K';
      case 'vodUpload': return 'Upload VOD J2K';
      case 'maxParityTrim': return 'Trim MAX J2K Interlace';
      case 'maxTrim': return 'Trim MAX J2K Progressive';
      case 'maxParityMetadata': return 'Metadata MAX J2K Interlace';
      case 'maxMetadata': return 'Metadata MAX J2K Progressive';
      case 'maxParityUpload': return 'Upload MAX J2K Interlace';
      case 'maxUpload': return 'Upload MAX J2K Progressive';
      case 'maxParityOvertimeTrim': return 'Trim Overtime MAX J2K Interlace';
      case 'maxParityOvertimeMetadata': return 'Metadata Overtime MAX J2K Interlace';
      case 'linearTrim': return 'Trim Linear';
      case 'linearMetadata': return 'Metadata Linear';
      case 'linearUpload': return 'Upload Linear';
      case 'archiveUpload': return 'Upload Archives Main';
      case 'archiveOvertimeUpload': return 'Upload Archives Overtime';
      case 'c3Upload': return 'Upload C3';
      case 'c3ExternalOutput': return 'C3 Router';
      case 'backupUpload': return 'Upload Backups';
      case 'finished': return 'Finished';
      case 's3downloadVideo': return 'Download S3 Video';
      case 'loadVideoToPlayback': return 'Open video to playback';
      case 'vodStartManualUpload': return 'Approve VOD J2K Upload';
      case 'maxParityStartManualUpload': return 'Approve MAX J2K Interlace Upload';
      case 'maxStartManualUpload': return 'Approve MAX J2K Progressive Upload';
      case 'linearStartManualUpload': return 'Approve Linear Upload';
      case 'restripe': return 'Restripe';
      default:
        return type;
    }
    return type;
  }

  setTimecodes(timecodes: any): void {
    if (this.timecodeRef && timecodes) {
      const type = this.preview.settings?.tcSource || 'srt_ts';
      const tc = timecodes.playback || timecodes[type];
      this.timecodeRef.nativeElement.innerHTML = tc || '00:00:00:00';
      this.progressBarRef?.setValue(tc);
      this.inOutPointsRefs?.forEach(element => element.setTimecode(tc));
      this.inOutTranscodePointsRefs?.forEach(element => element.setTimecode(tc));
      if (timecodes.playbackTime) {
        this.inOutTranscodePointsRefs?.forEach(element => element.setFFmpegTimecode(timecodes.playbackTime));
      }
      this.playbackActionsRef?.setTimecode(tc);
    }
  }

  setCC(cc: any): void {
    if (this.ccRef) {
      this.ccRef?.forEach(element => element.setCaptions(cc));
    }
  }

  setAudioLevel(levels: any): void {
    if (this.audioLevels && levels) {
      this.audioLevels.setAudioLevel(levels);
    }
  }

  filterRecordEvents(): void {
    if (this.search.text.length) {
      let regex: RegExp | undefined;
      try {
        regex = new RegExp(this.search.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      } catch {
        regex = undefined;
      }
      this.displayRecordEvents = regex
        ? (this.recordEvents?.filter(item => !!item.text?.match(regex!)) || [])
        : [];
    } else {
      this.displayRecordEvents = this.recordEvents;
    }
    if (this.search.serverId.length) {
      this.displayRecordEvents = this.displayRecordEvents.filter(event => event.recordServerId === this.search.serverId);
    }
  }

  filterRecordEventsByServerId(id: string): void {
    if (this.search.serverId !== id) {
      this.search.serverId = id;
    } else {
      this.search.serverId = '';
    }
    this.filterRecordEvents();
  }

  getServerType(id: string): string {
    return this.record?.servers?.find(server => server.id === id)?.type || '';
  }

  getServerName(id: string): string {
    return this.record?.servers?.find(server => server.id === id)?.name || '';
  }

  getEventSource(event: RecordEvent): string {
    return event.source === 'central' ? 'Server' : (event.source || '');
  }

  updateActions(): void {
    // Iris Upload
    this.actions.irisUpload.show = !!this.record?.servers?.some(server => !!server?.tasks.find(task => task.key === 'irisStartManualUpload'));
    this.actions.irisUpload.enabled = true;
    this.actions.irisUpload.done = !!this.record?.servers?.some(server => server?.tasks.find(task => task.key === 'irisStartManualUpload')?.status === 'done');
    // Iris Overtime Upload
    this.actions.irisOvertimeUpload.show = !!this.record?.servers?.some(server => !!server?.tasks.find(task => task.key === 'irisOvertimeStartManualUpload'));
    this.actions.irisOvertimeUpload.enabled = true;
    this.actions.irisOvertimeUpload.done = !!this.record?.servers?.some(server => server?.tasks.find(task => task.key === 'irisOvertimeStartManualUpload')?.status === 'done');
    // Reset
    this.actions.resetTasks.show = !!this.record?.servers?.some(server => !!server?.tasks.find(task => task.key === 'vodTrim'));
    this.actions.resetTasks.enabled = true;
    this.actions.resetTasks.done = !!this.record?.servers?.some(server => server?.tasks.find(task => task.key === 'reset')?.status === 'done');
    // Reset Overtime
    this.actions.resetOvertimeTasks.show = !!this.record?.servers?.some(server => !!server?.tasks.find(task => task.key === 'maxParityOvertimeTrim'));
    this.actions.resetOvertimeTasks.enabled = true;
    this.actions.resetOvertimeTasks.done = !!this.record?.servers?.some(server => server?.tasks.find(task => task.key === 'reset')?.status === 'done');
    
    // Display
    
    // Preview
    this.display.preview = !!this.record?.servers?.some(server => !!server?.tasks.find(task => task.key === 'recording')) || !!this.record?.servers?.some(server => server.channels?.length);
    // S3 Download
    this.display.s3DownloadInput = !!this.record?.servers?.some(server => !!server?.tasks.find(task => task.key === 's3downloadVideo'));
  }

  /**
   * Actions
   */

  copyRecordId(): void {
    if (!this.record?.id) { return; }
    this.clipboard.copy(this.record.id);
    this.notificationService.success('Copied to clipboard', '', false);
  }

  back(): void {
    if (this.record) {
      this.router.navigate(['/', this.record.projectId, 'records']);
    }
  }

  changeStatus(status: RecordStatus): void {
    if (this.record?.id && this.record?.status) {
      this.record.status = status;
      this.recordsService.updateRecordStatus(this.record.id, status);
    }
  }

  duplicateRecord(): void {
    const record = cloneDeep(this.record);
    this.dialog.open(CreateRecordComponent, {data: {record: record}, disableClose: true});
  }

  cancelRecord(): void {
    if (this.record) {
      this.alertService.show(
        'Cancel record',
        'Would you like to cancel current record?',
        ['Yes', 'No'],
        0
      )
        .subscribe(response => {
          if (response === 0) {
            this.recordsService.cancelRecord(this.record!);
          }
        });
    }
  }

  restoreRecord(): void {
    if (this.record) {
      this.recordsService.restoreRecord(this.record);
    }
  }

  selectPreview(type: PreviewType, serverId: string | undefined): void {
    this.updateSelectPreview.next();
    this.preview.type = type;
    this.preview.serverId = serverId + '';
    const server = this.record?.servers?.find(item => item.id === this.preview.serverId);
    if (server) {
      this.preview.status = (this.preview.type === 'playback') ? this.servers[server.id]?.playbackStatus : this.servers[server.id]?.recordStatus;
      this.preview.clip = this.servers[server.id]?.clip;
      this.updatePreviewFlags();
      // Get Settings
      this.previewService.getSettings(this.record?.id!, [this.preview.serverId], this.preview.channel)
        .pipe(takeUntil(this.updateSelectPreview))
        .subscribe(settings => {
          this.preview.settings = settings;
          console.log('[Settings] ', settings);
        });
    }
  }

  updatePreviewFlags(): void {
    const server = this.record?.servers?.find(item => item.id === this.preview.serverId);
    if (!server) {
      return;
    }
    this.preview.channel = (this.preview.type === 'playback' ? server.playbackChannel : server.recordChannel) || 0;
    this.preview.isTrim = server.tasks.some(task => task.type?.toLowerCase().includes('trim') && task.type !== 'contentAcquisitionTrim');
    this.preview.isOvertimeTrim = server.tasks.some(task => task.type?.toLowerCase().includes('overtime'));
    this.preview.isManualUpload = server.tasks.some(task =>
      task.key === 'vodStartManualUpload' ||
      task.key === 'maxParityStartManualUpload' ||
      task.key === 'maxStartManualUpload' ||
      task.key === 'linearStartManualUpload' ||
      task.key === 'contentAcquisitionStartManualUpload'
    );
    this.preview.isS3Download = server.tasks.some(task => task.type === 's3downloadVideo' && task.status === 'waiting');
    // Keep optimistic "Downloading" state until the server moves the task out of 'waiting'
    if (!this.preview.isS3Download) {
      this.s3DownloadRequested = false;
    }
    this.preview.isS3DownloadInProgress = this.s3DownloadRequested || server.tasks.some(task => task.type === 's3downloadVideo' && task.status === 'inProgress');
    this.preview.isICap = server.tasks.some(task => task.type === 'startIcap');
  }

  changeAudioPair(pair: number): void {
    this.audio.pair = pair;
  }

  updateAudio(): void {
    this.audio.enable = !this.audio.enable;
  }

  updateVolume(value: any): void {
    this.audio.volume = value;
    console.log('[Volume] ', value);
    this.previewRef?.updateVolume(this.audio.volume);
  }

  openCCTypeMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.ccTypeMenuButton?.openMenu();
  }

  selectCCType(type: CcType): void {
    this.ccType = type;
    const index = this.ccTypes.indexOf(type);
    if (index !== -1) {
      this.ccTypes.splice(index, 1);
    } else {
      this.ccTypes.push(type);
    }
    this.ccTypes = this.ccTypes.sort();
  }

  createTasks(server: RecordServer): void {
    if (this.record?.id && server) {
      this.alertService.show(
        `Force Create Tasks`,
        'Would you like to force create tasks?',
        ['Yes', 'No'],
        0
      )
      .subscribe(response => {
        if (response === 0 && this.record?.id) {
          this.recordsService.createTasks(this.record.id, server.id);
        }
      });
    }
  }

  recreateServerInstance(server: RecordServer): void {
    if (this.record?.id && server) {
      this.alertService.show(
        `Re-create Server`,
        'Would you like to re-create AWS server?',
        ['Yes', 'No'],
        0
      )
      .subscribe(response => {
        if (response === 0 && this.record?.id) {
          this.recordsService.recreateServerInstance(this.record.id, server.id);
        }
      });
    }
  }

  reloadAgentData(server: RecordServer): void {
    if (this.record?.id && server) {
      this.alertService.show(
        `Reload Data`,
        'Would you like to reload server-agent data?',
        ['Yes', 'No'],
        0
      )
      .subscribe(response => {
        if (response === 0 && this.record?.id) {
          this.recordsService.refreshAgentData(this.record.id, server.id);
        }
      });
    }
  }

  restartAgent(server: RecordServer): void {
    if (this.record?.id && server) {
      this.alertService.show(
        `Restart Agent`,
        'Would you like to restart agent?',
        ['Yes', 'No'],
        0
      )
      .subscribe(response => {
        if (response === 0 && this.record?.id) {
          this.recordsService.restartAgent(this.record.id, server.id);
        }
      });
    }
  }

  startRecord(serverId?: string): void {
    if (this.record?.id) {
      let alertComment = '';
      let recordBody = null;
      const server = this.record?.servers?.find(item => item.id === serverId);
      const iCapTask = server?.tasks.find(task => task.type === 'startIcap');
      if (iCapTask?.body?.icap?.start && iCapTask?.body?.icap?.end) {
        recordBody = {
          segments:[{
            inPoint: iCapTask?.body?.icap?.start,
            outPoint: iCapTask?.body?.icap?.end,
            crashRecord: true
         }]
        };
        alertComment += ` (start: "${iCapTask?.body?.icap?.start}", end: "${iCapTask?.body?.icap?.end}")`
      }
      this.alertService.show(
        'Start record',
        'Would you like to start current record?' + alertComment,
        ['Yes', 'No'],
        0
      )
      // .pipe(
      //   filter(response => response === 0),
      //   switchMap(() => this.previewService.goToStart(this.record!.id!, [this.preview.serverId!])),
      //   switchMap(() => this.recordsService.startRecord(this.record!.id!, [this.preview.serverId!], recordBody)),
      //   switchMap(() => this.previewService.play(this.record!.id!, [this.preview.serverId!]))
      // )
      // .subscribe(response => {});
      .subscribe(response => {
        if (response === 0) {
          const serverIds = serverId ? [serverId] : (this.record?.servers?.map(server => server.id) || []);
          this.recordsService.startRecord(this.record!.id!, serverIds, recordBody);
        }
      });
    }
  }

  startTestRecord(preivew?: PreviewModel, event?: MouseEvent): void {
    // Stop event for not startRecord() function call
    event?.preventDefault();
    event?.stopPropagation();

    // Get params
    const serverId = preivew?.serverId as string;
    // const settings = preivew?.settings;
    // const recordFilePath = settings?.channels?.[0]?.driveAssignments?.[0]?.primaryPath;
    // const filenameTemplate = settings?.nameTemplates?.[0]?.fileName || 'TEST_ROADRUNNER_BILL_MAHER_PRIMARY_%D_%t';
    // const fullPath = `${recordFilePath}\\${filenameTemplate}.mxf`;

    // Get filename
    const server = this.record?.servers?.find(item => item.id === serverId);
    const serverName = server?.name?.toLowerCase().includes('backup') ? 'BACKUP' : 'PRIMARY';
    const testFilename = `Z:\\Avid MediaFiles\\MXF\\cinedeck.5\\TEST_ROADRUNNER_BILL_MAHER_${serverName}_%D_%t.mxf`;

    // Open dialog to select filename
    this.dialog.open(RecordTestFilenameComponent, {
      data: {filename: testFilename},
      disableClose: true
    })
      .afterClosed()
      .subscribe(response => {
        if (response && response.filename) {
          let recordBody = {
            segments:[{
              crashRecord: true,
              filenames: [{
                encoderIndex: 0,
                filename: response.filename
              }]
            }]
          };
          const serverIds = [serverId];
          this.recordsService.startRecord(this.record!.id!, serverIds, recordBody);
        }
      });

  }

  resetRecord(serverId?: string): void {
    if (this.record?.id) {
      this.alertService.show(
        'Reset record',
        'Would you like to reset current record?',
        ['Yes', 'No'],
        0
      )
      .subscribe(response => {
        if (response === 0) {
          const serverIds = serverId ? [serverId] : (this.record?.servers?.map(server => server.id) || []);
          this.recordsService.resetRecord(this.record!.id!, serverIds);
        }
      });
    }
  }

  stopRecord(serverId?: string): void {
    if (this.record?.id) {
      this.alertService.show(
        'Stop record',
        'Would you like to stop current record?',
        ['Yes', 'No'],
        0
      )
      .subscribe(response => {
        if (response === 0) {
          const serverIds = serverId ? [serverId] : (this.record?.servers?.map(server => server.id) || []);
          this.recordsService.stopRecord(this.record!.id!, serverIds);
        }
      });
    }
  }

  resetRecordTasks(programs: ('main' | 'overtime')[]): void {
    if (this.record?.id) {
      this.alertService.show(
        'Reset record tasks',
        'Would you like to reset tasks for current record?',
        ['Yes', 'No'],
        0
      )
      .subscribe(response => {
        if (response === 0) {
          this.recordsService.resetRecordTasks(this.record!.id!, programs);
        }
      });
    }
  }

  approveRecord(isOvertime: boolean = false): void {
    if (this.record?.id) {
      this.alertService.show(
        'Approve',
        `Approve ${isOvertime ? 'Overtime' : 'Main'} Program and start uploading to Iris`,
        ['Approve', 'Cancel'],
        0
      )
      .subscribe(response => {
        if (response === 0) {
          const serverIds = this.record?.servers?.map(server => server.id) || [];
          const key = isOvertime ? 'irisOvertimeStartManualUpload' : 'irisStartManualUpload';
          this.recordsService.startManualTask(this.record!.id!, serverIds, key);
        }
      });
    }
  }

  finishRecord(): void {
    if (this.record) {
      this.alertService.show(
        'Finish record',
        'Would you like to finish current record?',
        ['Yes', 'No'],
        0
      )
      .subscribe(response => {
        if (response === 0) {
          this.recordsService.finishRecord(this.record!);
        }
      });
    }
  }

  s3DownloadFile(): void {
    if (this.record?.id) {
      this.preview.isS3DownloadInProgress = true;
      this.s3DownloadRequested = true;
      this.recordsService.s3Download(this.record.id, this.serverIds.value, this.s3DownloadUrl)
        .subscribe(response => {
          this.notificationService.success(`File download started successfully`, '', true);
        });
    }
  }

}
